import { Alert } from '@mui/material';
import { useKeycloak } from '@react-keycloak/web';
import { Fragment, useCallback, useEffect, useState } from 'react';
import ReactPaginate from 'react-paginate';
import { translate } from 'react-switch-lang';
import { Card, CardBody, CardHeader, Col, Container, Row } from 'reactstrap';
import { DeleteButtonIcon } from 'src/component/Buttons/DeleteButtonIcon';
import SweetAlert from 'sweetalert2';
import { useHttpClient } from '../../../../../../security/hooks/axiosProvider';
import Breadcrumb from '../../../../breadcrumb';
import styles from '../../../../styles/style-setting.module.scss';
import CreateRegion from '../create-region/create-region';
import EditRegion from '../edit-region/edit-region';
import ActivityColumns from './table-header/activity-columns';

// number of activities per page
const NUMBER_OF_REGIONS_PER_PAGE = 15;

const ListRegionsPage = (props) => {
  const [regions, setRegions] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState('');
  const [countries, setCountries] = useState([]);
  const httpClient = useHttpClient(true);
  const [pageNumber, setPageNumber] = useState(0);
  const activityPerPage = NUMBER_OF_REGIONS_PER_PAGE;
  const changePage = ({ selected }) => {
    setPageNumber(selected);
  };

  const keycloak = useKeycloak().keycloak;
  const [role, setRole] = useState('');
  const [search, setSearch] = useState('');
  const [data, setData] = useState([]);

  const [spinner, setSpinner] = useState({ state: false, id: '' });
  const pageCount = Math.ceil(
    (search === '' ? regions : regions.filter((activity) => activity.name.includes(search))).length / activityPerPage
  );

  const getRegions = useCallback(
    (country = '') => {
      httpClient.current?.get(`/api/region/all`).then((response) => {
        setRegions(response.data);
        buildData(response.data, search, country);
      });
    },
    [httpClient, search]
  );
  
  const buildData = (newRegion, searchCriteria, countryCriteria) => {
    const pagesVisited = pageNumber * NUMBER_OF_REGIONS_PER_PAGE;
    const filteredRegions = newRegion.filter((region) =>
      region.name.toUpperCase().includes(searchCriteria.toUpperCase())
    );
    const filteredByCountry = countryCriteria
      ? filteredRegions.filter((region) => region.country.id == countryCriteria)
      : filteredRegions;
    const restPagination = filteredByCountry.slice(pagesVisited, pagesVisited + activityPerPage);
    const newData = restPagination.map((region) => {
      let action = (
        <div>
          <span>
            <EditRegion region={region} regions={getRegions} />
          </span>
          <span>
            <DeleteButtonIcon
              title={props.t('delete_region')}
              onClick={() =>
                SweetAlert.fire({
                  title: props.t('you_delete'),
                  text: props.t('not_recover'),
                  icon: 'warning',
                  showCancelButton: true,
                  confirmButtonText: 'Ok',
                  cancelButtonText: props.t('cancel'),
                  reverseButtons: true,
                }).then((result) => {
                  if (result.value) {
                    deleteRegion(region.id);
                    SweetAlert.fire(props.t('Deleted!'), props.t('isDeleted'), 'success');
                  } else {
                    SweetAlert.fire(props.t('isNotDeleted'));
                  }
                })
              }
              className={`fa fa-trash ${styles['trash']}`}
            />
          </span>
        </div>
      );
      let status =
        spinner.state === true && spinner.id === region.id ? (
          <div className={`loader-box  ${styles['spinner']}`}>
            <div className="loader-2"></div>
          </div>
        ) : (
          <input
            type="checkbox"
            disabled={role === 'admin'}
            name="status"
            checked={region?.valid}
            onChange={(e) => handleCheckBox(e, region)}
          />
        );

      return {
        id: region.id,
        name: region.name,
        status: status,
        action: action,
      };
    });
    setData(newData);
  };

  const deleteRegion = async (regionId) => {
    await httpClient.current?.delete(`/api/region/${regionId}`);
    getRegions();
  };

  const handleCheckBox = async (event, activity) => {
    setSpinner({ state: true, id: activity.id });
    const activityValidation = { id: activity.id, name: activity.name, valid: event.target.checked };
    const response = httpClient.current?.put(`/api/country/update`, activityValidation);
    if ((await response).status === 200) {
      getRegions();
      setSpinner({ state: false, id: activity.id });
    }
  };
  const fetchCountries = () => {
    httpClient.current?.get(`/api/country/all`).then((response) => {
      setCountries(response.data);
    });
  };
  useEffect(() => {
    getRegions();

    if (keycloak.authenticated) {
      keycloak.hasResourceRole('ROLE_AGRISED_ADMIN_SUPER')
        ? setRole('super admin')
        : keycloak.hasResourceRole('ROLE_AGRISED_ADMIN')
        ? setRole('admin')
        : setRole('user');
    }
    fetchCountries();
  }, [getRegions, keycloak]);

  useEffect(() => {
    getRegions(selectedCountry);
  }, [getRegions, selectedCountry,pageNumber]);

  useEffect(() => {
    buildData(regions, search,selectedCountry);
  }, [regions, search, pageNumber]);

  const handleSearch = (event) => {
    setSearch(event.target.value);
    buildData(regions, event.target.value, selectedCountry);
    changePage({ selected: 0 });
  };
  return (
    <div>
      <Fragment>
        <Breadcrumb parent={props.t('country_url')} url={'settings/country'} />
        <Container fluid={true}>
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader>
                  <div className="float-right">
                    <CreateRegion regions={getRegions} />
                  </div>
                </CardHeader>
                <CardBody>
                  <div className="d-flex mb-2">
                    <input
                      className="form-control col-md-6 mr-3 "
                      onChange={handleSearch}
                      value={search}
                      placeholder={props.t('search_region')}
                    />
                    <select
                      className="form-control col-md-6"
                      value={selectedCountry}
                      onChange={(event) => setSelectedCountry(event.target.value)}
                    >
                      <option value="">{props.t('all_countries')}</option>
                      {countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {data.length === 0 ? (
                    <Alert variant="outlined" severity="info">
                      {props.t('no_country')}
                    </Alert>
                  ) : (
                    <ActivityColumns data={data} />
                  )}
                  <br />
                  <div className="float-right">
                    {regions.length > NUMBER_OF_REGIONS_PER_PAGE && (
                      <ReactPaginate
                        previousLabel="<"
                        nextLabel=">"
                        pageCount={pageCount}
                        onPageChange={changePage}
                        containerClassName={styles['paginationButton']}
                        previousLinkClassName={styles['previousBtn']}
                        nextLinkClassName={styles['nextBtn']}
                        disabledClassName={styles['paginationDisabled']}
                        activeClassName={styles['paginationActive']}
                      />
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </Fragment>
    </div>
  );
};
export default translate(ListRegionsPage);
